import { useEffect, useRef } from 'react';

const CHANNEL = 'smartcfo-accounting-period';

let channel;

function getChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL);
  return channel;
}

/**
 * Avisa a las otras pestañas del mismo origen que un mes se abrió o se cerró.
 * El canal queda abierto: cerrarlo justo después de postMessage pierde el aviso.
 * BroadcastChannel no entrega el mensaje a la pestaña que lo publicó.
 */
export function notifyAccountingPeriodChanged({ companyId, periodType, month, year, opened }) {
  if (!companyId) return;
  const payload = {
    companyId: String(companyId),
    periodType: periodType || null,
    month: month == null ? null : Number(month),
    year: year == null ? null : Number(year),
    opened: typeof opened === 'boolean' ? opened : null,
  };
  getChannel()?.postMessage(payload);
}

export function subscribeAccountingPeriodChanged(handler) {
  const current = getChannel();
  if (!current) return () => {};
  const onMessage = (event) => {
    if (event?.data) handler(event.data);
  };
  current.addEventListener('message', onMessage);
  return () => current.removeEventListener('message', onMessage);
}

export function dateFallsInPeriod(isoDate, month, year) {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getMonth() + 1 === Number(month) && parsed.getFullYear() === Number(year);
}

/** Vuelve a consultar al volver a la pestaña, si el aviso entre pestañas no llegó. */
export function useRefreshWhenVisible(enabled, onVisible) {
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;
    const onChange = () => {
      if (document.visibilityState === 'visible') onVisibleRef.current();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [enabled]);
}

/**
 * Ejecuta `onChange` cuando otra pestaña abre o cierra un mes de esta empresa.
 * Si `periodType` viene, ignora los cambios de los otros tipos.
 */
export function useOnAccountingPeriodChanged(companyId, onChange, periodType) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!companyId) return undefined;
    return subscribeAccountingPeriodChanged((message) => {
      if (String(message?.companyId) !== String(companyId)) return;
      if (periodType && message?.periodType && message.periodType !== periodType) return;
      onChangeRef.current(message);
    });
  }, [companyId, periodType]);
}
